import { VoiceStartupFallbackSchema } from './VoiceStartupFallback.js';
import { z } from 'zod';
import { OutboundCallSummarySchema } from './OutboundAllocations.js';
import { ConversationModeSchema, ConversationProviderSchema, IsoDateTimeSchema, QualityStatusSchema, SessionStatusSchema, TokenUsageSchema, UuidSchema, VoiceSchema, } from './common.js';
import { createPaginatedListResponseSchema } from './PaginatedListResponse.js';
import { QualityProcessingStatusSchema } from './QualityProcessing.js';
export const CreateSessionRequestSchema = z.object({
    voiceStartupFallback: VoiceStartupFallbackSchema.optional(),
    projectId: UuidSchema,
    organizationId: UuidSchema,
    configVersion: z.number().multipleOf(1).nonnegative(),
    voice: VoiceSchema.optional(),
    participantId: z.string().min(1).optional(),
    moderatorId: z.string().min(1).optional(),
    source: z.string().min(1).optional(),
    /**
     * The vendor the session's conversation will run on, copied from the study
     * at creation. Omitted by callers that predate the field; the row then
     * receives the legacy value when a call id is recorded.
     */
    conversationProvider: ConversationProviderSchema.optional(),
    /** Voice or a text-only chat; omitted by callers that predate chat, which means voice. */
    conversationMode: ConversationModeSchema.optional(),
    isDemo: z.boolean().optional(),
    prolificPid: z.string().min(1).optional(),
    prolificSessionId: z.string().min(1).optional(),
    cintRid: z.string().min(1).optional(),
});
export const UpdateSessionRequestSchema = z.object({
    status: SessionStatusSchema.optional(),
    callId: z.string().min(1).nullable().optional(),
    connectedAt: IsoDateTimeSchema.nullable().optional(),
    endedAt: IsoDateTimeSchema.nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    durationWarning: z.boolean().optional(),
    warningAt: IsoDateTimeSchema.nullable().optional(),
    durationMinutes: z.number().multipleOf(1).nonnegative().nullable().optional(),
    durationSeconds: z.number().multipleOf(1).nonnegative().nullable().optional(),
});
export const UpdateUsageRequestSchema = z.object({
    tokenUsage: TokenUsageSchema,
    usageFinalized: z.boolean().optional(),
    durationMinutes: z.number().multipleOf(1).nonnegative().nullable().optional(),
});
// Effective quality filters include failed processing without adding a stored quality status.
export const SessionQualityFilterSchema = z.union([QualityStatusSchema, z.literal('qa_failed')]);
export const SessionQualityFiltersSchema = z
    .union([
    z.array(SessionQualityFilterSchema),
    z
        .string()
        .transform((value) => value.split(','))
        .pipe(z.array(SessionQualityFilterSchema)),
])
    .transform((values) => [...new Set(values)]);
export const SessionsQuerySchema = z.object({
    projectId: UuidSchema.optional(),
    organizationId: UuidSchema.optional(),
    participantIds: z
        .union([
        z.array(z.string().min(1)).max(500),
        z
            .string()
            .transform((value) => value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean))
            .pipe(z.array(z.string().min(1)).max(500)),
    ])
        .optional(),
    source: z.string().min(1).optional(),
    cintRid: z.string().min(1).max(512).optional(),
    status: SessionStatusSchema.optional(),
    qualityStatus: QualityStatusSchema.optional(),
    qualityFilters: SessionQualityFiltersSchema.optional(),
    qualityProcessingStatus: QualityProcessingStatusSchema.optional(),
    excludeQualityProcessingStatus: QualityProcessingStatusSchema.optional(),
    startedFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    startedTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    sortBy: z.enum(['createdAt', 'startedAt', 'durationSeconds']).prefault('createdAt'),
    sortDirection: z.enum(['asc', 'desc']).prefault('desc'),
    excludePreview: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((value) => value === undefined ? undefined : typeof value === 'boolean' ? value : value === 'true'),
    excludeDemo: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((value) => value === undefined ? undefined : typeof value === 'boolean' ? value : value === 'true'),
    eligibleForAnalysis: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .optional()
        .transform((value) => value === undefined ? undefined : typeof value === 'boolean' ? value : value === 'true'),
    limit: z.number().multipleOf(1).positive().max(500).prefault(20),
    offset: z.number().multipleOf(1).nonnegative().prefault(0),
});
export const SessionResponseSchema = z
    .object({
    outboundCall: OutboundCallSummarySchema.nullable().optional(),
    id: UuidSchema,
    projectId: UuidSchema,
    organizationId: UuidSchema,
    configVersion: z.number().multipleOf(1).nonnegative(),
    participantId: z.string().nullable(),
    callId: z.string().nullable(),
    /** Successor session created after a startup fallback; absent on older service versions. */
    startupReplacedBy: UuidSchema.nullable().optional(),
    /** Null on rows that predate the field; readers treat that as VAPI. */
    conversationProvider: z.string().nullable().optional(),
    /** Absent on older service versions; readers treat that as voice. */
    conversationMode: ConversationModeSchema.optional(),
    voice: VoiceSchema,
    status: SessionStatusSchema,
    tokenUsage: TokenUsageSchema.nullable(),
    usageFinalized: z.boolean(),
    durationMinutes: z.number().multipleOf(1).nullable(),
    durationSeconds: z.number().multipleOf(1).nullable(),
    durationWarning: z.boolean(),
    warningAt: IsoDateTimeSchema.nullable(),
    errorMessage: z.string().nullable(),
    endedReason: z.string().nullable().optional(),
    startedAt: IsoDateTimeSchema,
    connectedAt: IsoDateTimeSchema.nullable(),
    endedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    recordingUrl: z.string().nullable(),
    conversationCost: z.number().nullable().optional(),
    /** @deprecated Use conversationCost. */
    vapiCost: z.number().nullable().optional(),
    successEvaluation: z.number().nullable(),
    qualityStatus: QualityStatusSchema,
    source: z.string(),
    isDemo: z.boolean(),
    prolificPid: z.string().nullable(),
    prolificSessionId: z.string().nullable(),
    cintRid: z.string().nullable(),
    /**
     * User's explicit inclusion choice. NULL = follow default rule (completed → included, otherwise excluded).
     */
    includedInAnalysis: z.boolean().nullable(),
    qualityProcessingStatus: QualityProcessingStatusSchema.optional(),
    providerIngestionStatus: z.string().optional(),
    qualityProcessingAttempts: z.number().multipleOf(1).nonnegative().optional(),
    qualityProcessingError: z.string().nullable().optional(),
    qualityProcessedAt: IsoDateTimeSchema.nullable().optional(),
})
    .refine((value) => value.conversationCost !== undefined || value.vapiCost !== undefined, {
    message: 'Conversation cost or its legacy alias is required',
    path: ['conversationCost'],
});
export const SessionPaginatedListResponseSchema = createPaginatedListResponseSchema(SessionResponseSchema);
/** Aggregate only; cleanup callers never receive participant or transcript data. */
export const PreviewCleanupReadinessResponseSchema = z.object({
    active: z.number().int().nonnegative(),
    postCallPending: z.number().int().nonnegative(),
    postCallFailed: z.number().int().nonnegative(),
});
export const SessionStatsResponseSchema = z.object({
    // Legacy aliases (kept one rollout window). totalCount=visibleCount, completedCount=completedQualityCount.
    totalCount: z.number().multipleOf(1).nonnegative(),
    completedCount: z.number().multipleOf(1).nonnegative(),
    abandonedCount: z.number().multipleOf(1).nonnegative(),
    callCompletedCount: z.number().multipleOf(1).nonnegative(),
    averageDurationMinutes: z.number().nullable(),
    completionRate: z.number(),
    lastSessionAt: IsoDateTimeSchema.nullable(),
    // Optional during rollout so consumers remain compatible with older interviews-data revisions.
    lastCompletedAt: IsoDateTimeSchema.nullable().optional(),
    // New canonical counts under the inclusion-control rules.
    sessionCount: z.number().multipleOf(1).nonnegative(),
    visibleCount: z.number().multipleOf(1).nonnegative(),
    conductedCount: z.number().multipleOf(1).nonnegative(),
    completedQualityCount: z.number().multipleOf(1).nonnegative(),
    includedCount: z.number().multipleOf(1).nonnegative(),
});
export const BatchStatsRequestSchema = z.object({
    projectIds: z.array(UuidSchema),
    includePreview: z.boolean().optional(),
    excludeDemo: z.boolean().optional(),
});
export const BatchStatsResponseSchema = z.record(UuidSchema, z.object({
    // Legacy aliases (count=visibleCount, completedCount=completedQualityCount).
    count: z.number().multipleOf(1).nonnegative(),
    completedCount: z.number().multipleOf(1).nonnegative(),
    lastAt: IsoDateTimeSchema.nullable(),
    // Optional during rollout so consumers remain compatible with older interviews-data revisions.
    lastCompletedAt: IsoDateTimeSchema.nullable().optional(),
    sessionCount: z.number().multipleOf(1).nonnegative(),
    visibleCount: z.number().multipleOf(1).nonnegative(),
    conductedCount: z.number().multipleOf(1).nonnegative(),
    completedQualityCount: z.number().multipleOf(1).nonnegative(),
    includedCount: z.number().multipleOf(1).nonnegative(),
}));
export const UpdateSessionInclusionRequestSchema = z.object({
    includedInAnalysis: z.boolean().nullable(),
});
export const BulkUpdateSessionInclusionRequestSchema = z.object({
    ids: z.array(UuidSchema).min(1).max(200),
    includedInAnalysis: z.boolean().nullable(),
});
export const BulkUpdateSessionInclusionResponseSchema = z.object({
    sessions: z.array(SessionResponseSchema),
});
export const UpdateSessionProviderDataRequestSchema = z
    .object({
    conversationCost: z.number().optional(),
    /** @deprecated Use conversationCost. */
    vapiCost: z.number().optional(),
    recordingUrl: z.string().nullable().optional(),
    durationSeconds: z.number().multipleOf(1).nonnegative().optional(),
    successEvaluation: z.number().optional(),
})
    .strict()
    .refine((value) => value.conversationCost === undefined ||
    value.vapiCost === undefined ||
    value.conversationCost === value.vapiCost, { message: 'Conflicting conversationCost and vapiCost' });
//# sourceMappingURL=Sessions.js.map